import React from "react";
import Container from "Components/Container/Container";
import { useContext, useState } from "react";
import { CartContext } from "context/Cart/CartContext";
import { LoginContext } from "context/Auth/LoginContext";
import { Trash, Question, ShoppingCart, Clock } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import Spinner from "Components/RequestHandler/Spinner";
import IMAGE_URL from "Utilities/IMAGE_URL";
import { useTranslation } from "react-i18next";
import usePost from "Hooks/usePost";

const Cart = () => {
  const { t } = useTranslation();
  const {
    cart,
    removeFromCart,
    isItemLoading,
    deleteError,
    updateCartStatus,
    isPutLoading,
  } = useContext(CartContext);
  const { user, token } = useContext(LoginContext);
  const [checkoutError, setCheckoutError] = useState(null);
  const { postData: postNotify } = usePost();

  // Add this debug logging
  console.log("Cart contents:", cart);
  console.log("Books:", cart?.books);
  console.log("Courses:", cart?.courses);

  const handleCheckout = async () => {
    try {
      setCheckoutError(null);

      console.log("Updating cart status to pending...");
      // Update cart status to pending
      const success = await updateCartStatus("pending");

      if (!success) {
        throw new Error("Failed to update cart status. Please try again.");
      }

      console.log(
        "Cart status updated successfully. Sending email notifications..."
      );

      // Notify backend to send emails to admin and user (non-blocking)
      try {
        await postNotify("cart/notify-checkout", {}, token);
      } catch (notifyErr) {
        console.error("Failed to notify checkout via email:", notifyErr);
      }

      console.log(
        "Notifications sent (or attempted). Preparing WhatsApp message..."
      );
      // Prepare message with order details
      let message = `*📝 NEW ORDER REQUEST*\n\n`;
      message += `Hello, I'm *${user?.fullName || "a customer"}*.\n`;
      message += `I would like to place an order for the following items:\n\n`;

      // Add books section if there are books
      if (cart?.books?.length > 0) {
        message += `*📚 BOOKS 📚*\n`;
        message += `------------------\n`;
        cart.books.forEach((book, index) => {
          message += `${index + 1}. *${book.title}*\n   Price: $${
            book.price
          }\n`;
        });
        message += `\n`;
      }

      // Add courses section if there are courses
      if (cart?.courses?.length > 0) {
        message += `*🎓 COURSES 🎓*\n`;
        message += `------------------\n`;
        cart.courses.forEach((course, index) => {
          message += `${index + 1}. *${course.title}*\n   Price: $${
            course.price
          }\n   Sessions: ${course.sessions?.length || 0}\n`;
        });
        message += `\n`;
      }

      // Add total and contact request
      message += `*💰 ORDER SUMMARY 💰*\n`;
      message += `------------------\n`;
      message += `*Total Amount: $${cart?.total?.toFixed(2) || 0}*\n\n`;
      message += `Thank you!`;

      // Encode the message for URL
      const encodedMessage = encodeURIComponent(message);

      // WhatsApp link with phone number
      const whatsappLink = `https://wa.me/+96171121461?text=${encodedMessage}`;

      // Open WhatsApp in a new tab
      window.open(whatsappLink, "_blank");
    } catch (error) {
      console.error("Checkout error details:", error);
      setCheckoutError(error.message);
    }
  };

  // Check if cart is empty
  const isCartEmpty =
    !cart ||
    ((!cart.books || cart.books.length === 0) &&
      (!cart.courses || cart.courses.length === 0));

  // Empty cart UI
  if (isCartEmpty) {
    return (
      <div className="mb-32">
        <Container>
          <div className="py-6 md:py-10">
            <h1 className="text-2xl md:text-3xl text-center font-title">
              {t("cart.cart")}
            </h1>
          </div>

          <div className="flex flex-col items-center justify-center py-10 md:py-16">
            <div className="bg-gray-100 rounded-full p-6 md:p-8 mb-4 md:mb-6">
              <ShoppingCart
                size={60}
                weight="thin"
                className="text-gray-500 sm:hidden"
              />
              <ShoppingCart
                size={80}
                weight="thin"
                className="text-gray-500 hidden sm:block"
              />
            </div>
            <h2 className="text-xl md:text-2xl font-title mb-3 md:mb-4">
              {t("cart.emptyCart")}
            </h2>
            <p className="text-gray-600 mb-6 md:mb-8 text-center max-w-md px-4 sm:px-0">
              {t("cart.continueShopping")}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 w-full sm:w-auto px-4 sm:px-0">
              <Link
                to="/books"
                className="bg-white border border-primary text-primary px-6 py-3 rounded-md font-medium hover:bg-gray-50 transition-colors text-center"
              >
                {t("books.shopBooks")}
              </Link>
              <Link
                to="/courses"
                className="bg-primary text-white px-6 py-3 rounded-md font-medium hover:bg-primary/90 transition-colors text-center"
              >
                {t("courses.allCourses")}
              </Link>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="mb-32">
      <Container>
        <div className="py-6 md:py-10">
          <h1 className="text-2xl md:text-3xl text-center font-title">
            {t("cart.cart")}
          </h1>
        </div>

        {/* Pending status banner */}
        {cart?.status === "pending" && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 md:p-4 mb-4 md:mb-6 flex items-center">
            <Clock
              size={20}
              weight="fill"
              className="text-yellow-500 mr-2 md:mr-3 flex-shrink-0 sm:size-24"
            />
            <div>
              <h3 className="font-medium text-base md:text-lg text-yellow-700">
                {t("modals.pendingCart.title")}
              </h3>
              <p className="text-yellow-600 text-sm md:text-base">
                {t("modals.pendingCart.message")}
              </p>
            </div>
          </div>
        )}

        {/* Show error if there is one */}
        {checkoutError && (
          <div className="bg-red-100 text-red-700 p-3 mb-5 rounded-md text-sm md:text-base">
            {checkoutError}
          </div>
        )}

        {cart && cart.books?.length > 0 && (
          <div>
            <h2 className="text-2xl md:text-3xl font-title">
              {t("books.books")}
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 mt-4 gap-3 md:gap-4">
              {cart.books?.map((book, index) => (
                <div
                  to={`/book/${book.slug}`}
                  className="flex flex-col sm:flex-row bg-gray-100 items-center justify-between p-3 rounded-xl border border-primary gap-3 sm:gap-x-10"
                  key={index}
                >
                  <div className="flex bg-white w-full sm:flex-[3] p-2 rounded-md items-center justify-between gap-x-4">
                    <div className="flex items-center gap-x-4">
                      <img
                        src={`${IMAGE_URL}/${book.image}`}
                        alt={book.title}
                        className="w-12 h-12 md:w-14 md:h-14 object-cover"
                      />
                      <h3 className="text-sm md:text-base line-clamp-2 md:line-clamp-none">
                        {book.title}
                      </h3>
                    </div>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        removeFromCart("book", book.slug);
                      }}
                      className="text-red-500 text-xl"
                      disabled={cart?.status === "pending"}
                      title={t("cart.removeFromCart")}
                    >
                      {isItemLoading("book", book.slug) ? (
                        <Spinner isSmall={true} />
                      ) : (
                        <Trash />
                      )}
                    </button>
                  </div>

                  <div className="bg-white w-full sm:flex-1 p-2 sm:p-3 rounded-md flex items-center justify-center">
                    <p className="text-lg md:text-xl font-medium">
                      {book.price}${" "}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {cart && cart.courses?.length > 0 && (
          <div className="mt-6 md:mt-10">
            <h2 className="text-2xl md:text-3xl font-title">
              {t("courses.courses")}
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 mt-4 gap-3 md:gap-4">
              {cart.courses?.map((course, index) => (
                <div
                  className="flex flex-col sm:flex-row bg-gray-100 items-center justify-between p-3 rounded-xl border border-primary gap-3 sm:gap-x-10"
                  to={`/course/${course.slug}`}
                  key={index}
                >
                  <div className="flex bg-white w-full sm:flex-[3] p-2 rounded-md items-center justify-between gap-x-4">
                    <div className="flex items-center gap-x-4">
                      <img
                        src={`${IMAGE_URL}/${course.image}`}
                        alt={course.title}
                        className="w-12 h-12 md:w-14 md:h-14 object-cover"
                      />
                      <span>
                        <h3 className="text-sm md:text-lg mb-0 md:mb-1 font-medium line-clamp-2 md:line-clamp-none">
                          {course.title}
                        </h3>
                        <p className="text-xs md:text-base">
                          {course.sessions?.length || 0} {t("courses.sessions")}
                        </p>
                      </span>
                    </div>

                    <button
                      onClick={() => removeFromCart("course", course.slug)}
                      className="text-red-500 text-xl"
                      disabled={cart?.status === "pending"}
                      title={t("cart.removeFromCart")}
                    >
                      {isItemLoading("course", course.slug) ? (
                        <Spinner isSmall={true} />
                      ) : (
                        <Trash />
                      )}
                    </button>
                  </div>

                  <div className="bg-white w-full sm:flex-1 p-2 sm:p-3 rounded-md flex items-center justify-center">
                    <p className="text-lg md:text-xl font-medium">
                      {course.price}${" "}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex flex-col sm:flex-row sm:justify-end items-center mt-8 md:mt-16 gap-4 sm:gap-x-4">
          <button className="text-dark relative font-medium text-base md:text-lg border border-primary px-4 md:px-8 py-3 md:py-4 rounded-md flex items-center gap-x-2 w-full sm:w-auto text-center justify-center">
            <p className="text-sm md:text-base">
              {t(
                "cart.createBundle",
                "Create Your Bundle and get 15% Discount"
              )}
            </p>
            <span className="bg-dark rounded-full block text-white p-1 group">
              <Question />
              <p className="absolute -top-10 right-0 bg-dark text-white px-2 py-1 rounded-md text-xs max-w-[200px] opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                {t("cart.bundleTooltip", "Add 10 courses to get 15% Discount")}
              </p>
            </span>
          </button>
          <button
            onClick={handleCheckout}
            className={`font-medium text-base md:text-lg text-white px-4 md:px-8 py-3 md:py-4 rounded-md border border-primary transition-colors w-full sm:w-auto ${
              cart?.status === "pending" ||
              isPutLoading ||
              (!cart.books?.length && !cart.courses?.length)
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-primary hover:bg-primary/90"
            }`}
            disabled={
              cart?.status === "pending" ||
              isPutLoading ||
              (!cart.books?.length && !cart.courses?.length)
            }
          >
            {isPutLoading ? (
              <Spinner isSmall={true} />
            ) : cart?.status === "pending" ? (
              t("cart.pendingApproval")
            ) : (
              t("cart.checkout")
            )}
          </button>
        </div>
        <div className="mt-8 md:mt-14 flex flex-col sm:flex-row sm:justify-end items-center">
          <p className="text-2xl md:text-3xl mb-2 sm:mb-0 sm:mr-3">
            {t("cart.total")}: {cart?.total?.toFixed(2) || 0}$
          </p>
          {cart?.status === "pending" && (
            <span className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm font-medium">
              {t("cart.pendingApproval")}
            </span>
          )}
        </div>
      </Container>
    </div>
  );
};

export default Cart;
